import React, { useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudUploadAlt, faTimes, faFile } from '@fortawesome/free-solid-svg-icons';
import { useModalDismiss } from '../../../hooks/useModalDismiss';

const STEP_LABELS = {
  ao: 'Avant-Vente (AO)', setup: 'Setup', terrain: 'Terrain', reporting: 'Reporting', closing: 'Closing',
};

const DocumentUploadModal = ({ isOpen, onClose, onUpload, step, loading, acceptedTypes }) => {
  const [label, setLabel] = useState('');
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const handleClose = () => {
    setLabel('');
    setFile(null);
    onClose();
  };
  // Editable form (file + libellé): only Escape and the explicit buttons dismiss it.
  const dismiss = useModalDismiss(handleClose, { backdropClose: false, enabled: isOpen });

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (!label.trim() || !file) return;
    onUpload({ step, label: label.trim(), file });
    setLabel('');
    setFile(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) setFile(dropped);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.4)',
    }}
      {...dismiss}
    >
      <div
        style={{
          background: '#fff', borderRadius: 12, width: '100%', maxWidth: 480,
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)', overflow: 'hidden',
          animation: 'modalSlideIn 0.2s ease',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 20px', borderBottom: '1px solid #f3f4f6',
        }}>
          <div>
            <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#111827' }}>Ajouter un document</div>
            <div style={{ fontSize: '0.68rem', color: '#9ca3af', marginTop: 1 }}>
              Étape: {STEP_LABELS[step] || step}
            </div>
          </div>
          <button onClick={handleClose} disabled={loading}
            style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', padding: 4 }}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px' }}>
          {/* Label */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: 5 }}>
              Libellé <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <input
              type="text"
              value={label}
              onChange={e => setLabel(e.target.value)}
              placeholder="Ex: Bon de commande signé"
              style={{
                width: '100%', fontSize: '0.82rem', padding: '8px 12px',
                border: '1px solid #d1d5db', borderRadius: 6, outline: 'none',
                transition: 'border-color 0.15s',
              }}
              onFocus={e => e.target.style.borderColor = '#dc2626'}
              onBlur={e => e.target.style.borderColor = '#d1d5db'}
            />
          </div>

          {/* File drop zone */}
          <div style={{ marginBottom: 8 }}>
            <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: 5 }}>
              Fichier <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <div
              onClick={() => fileRef.current?.click()}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              style={{
                border: `2px dashed ${dragOver ? '#dc2626' : file ? '#10b981' : '#d1d5db'}`,
                borderRadius: 10, padding: '24px 16px', textAlign: 'center',
                cursor: 'pointer', transition: 'all 0.15s',
                background: dragOver ? '#fef2f2' : file ? '#f0fdf4' : '#fafafa',
              }}
            >
              {file ? (
                <div>
                  <div style={{ width: 36, height: 36, borderRadius: 8, background: '#dcfce7', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 6 }}>
                    <FontAwesomeIcon icon={faFile} style={{ color: '#16a34a' }} />
                  </div>
                  <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#166534' }}>{file.name}</div>
                  <div style={{ fontSize: '0.68rem', color: '#6b7280', marginTop: 2 }}>
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                    <span style={{ margin: '0 6px', color: '#d1d5db' }}>·</span>
                    <span
                      onClick={e => { e.stopPropagation(); setFile(null); }}
                      style={{ color: '#dc2626', cursor: 'pointer', textDecoration: 'underline' }}
                    >
                      Changer
                    </span>
                  </div>
                </div>
              ) : (
                <div>
                  <FontAwesomeIcon icon={faCloudUploadAlt} size="lg" style={{ color: '#9ca3af', marginBottom: 8 }} />
                  <div style={{ fontSize: '0.82rem', color: '#6b7280' }}>
                    Glissez un fichier ici ou <span style={{ color: '#dc2626', fontWeight: 600 }}>parcourir</span>
                  </div>
                  <div style={{ fontSize: '0.65rem', color: '#d1d5db', marginTop: 4 }}>
                    PDF, Word, Excel, Images, ZIP · max 50 MB
                  </div>
                </div>
              )}
            </div>
            <input
              ref={fileRef}
              type="file"
              style={{ display: 'none' }}
              onChange={e => setFile(e.target.files[0] || null)}
              accept={acceptedTypes || undefined}
            />
          </div>
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: 8,
          padding: '14px 20px', borderTop: '1px solid #f3f4f6',
        }}>
          <button onClick={handleClose} disabled={loading}
            style={{ padding: '7px 16px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer' }}>
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !label.trim() || !file}
            style={{
              padding: '7px 16px', borderRadius: 6, border: 'none',
              background: (loading || !label.trim() || !file) ? '#e5e7eb' : '#dc2626',
              color: (loading || !label.trim() || !file) ? '#9ca3af' : '#fff',
              fontSize: '0.78rem', fontWeight: 600, cursor: (loading || !label.trim() || !file) ? 'default' : 'pointer',
            }}
          >
            {loading ? 'Upload en cours...' : 'Ajouter le document'}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes modalSlideIn {
          from { opacity: 0; transform: translateY(10px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
};

export default DocumentUploadModal;
